import { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import SelectField from "components/admin/SelectField";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useConfirm, useNotify } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { addDays, formatDate, formatNumber, toLocalInput } from "utils/format";

/*
 * Bố cục (người dùng: vận hành tạo mã quà cho sự kiện / KOL / đền bù):
 *   Bảng mã: mã (sao chép) · trạng thái bằng chữ · đã dùng / tối đa · quà (tên + hình) · hết hạn (còn N ngày) · thao tác có nhãn.
 *   Hộp tạo / sửa: mã · số người được dùng · hạn (chọn nhanh 7 / 30 / 90 ngày) · mã bang · quà
 *     → khung "Kiểm tra lại" + lý do chưa tạo được. Tạo hàng loạt: xong thì sao chép / tải file .txt.
 */

const EXPIRE_PRESETS = [7, 30, 90];

function copyText(text, notify) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => notify.success("Đã sao chép"))
    .catch(() => notify.error("Trình duyệt không cho sao chép"));
}

function downloadText(text, filename) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function daysLeft(expire) {
  const ms = new Date(expire).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `còn ${days} ngày`;
  return `còn ${Math.max(1, Math.floor(ms / 3600000))} giờ`;
}

function codeStatus(c) {
  if (c.expired) return { label: "Hết hạn", color: "default" };
  if (c.currentUser >= c.maxUser) return { label: "Hết lượt", color: "default" };
  return { label: "Còn dùng được", color: "success" };
}

function SummaryRow({ label, children }) {
  return (
    <MDBox
      display="flex"
      gap={{ xs: 0, sm: 2 }}
      py={0.5}
      flexDirection={{ xs: "column", sm: "row" }}
    >
      <MDTypography
        variant="button"
        fontWeight="bold"
        color="text"
        display="block"
        sx={{ width: { sm: 120 }, flexShrink: 0 }}
      >
        {label}
      </MDTypography>
      <MDBox flex={1} minWidth={0}>
        {typeof children === "string" ? (
          <MDTypography variant="button" display="block">
            {children}
          </MDTypography>
        ) : (
          children
        )}
      </MDBox>
    </MDBox>
  );
}

SummaryRow.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

/** Tạo / sửa một mã, hoặc tạo hàng loạt khi batch = true. */
function CodeDialog({ initial, batch, onClose, onSaved }) {
  const [run, busy] = useRunner();
  const notify = useNotify();
  const [form, setForm] = useState(() => ({
    code: initial?.code || "",
    maxUser: initial?.maxUser ?? (batch ? 1 : 100),
    gift: initial?.gift || [],
    expire: toLocalInput(initial?.expire || addDays(30)),
    isClanCode: initial?.isClanCode || false,
    prefix: "",
    length: 10,
    quantity: 50,
  }));
  const [codes, setCodes] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const code = form.code.trim();
  const prefix = form.prefix.trim().toUpperCase();
  const expireDate = new Date(form.expire);
  const blockers = [
    !batch && !code && "Chưa nhập mã.",
    !batch &&
      code &&
      !/^[a-zA-Z0-9]+$/.test(code) &&
      "Mã chỉ gồm chữ và số, không dấu, không khoảng trắng.",
    batch && prefix && !/^[A-Z0-9]+$/.test(prefix) && "Tiền tố chỉ gồm chữ và số.",
    batch && !(Number(form.quantity) >= 1 && Number(form.quantity) <= 500) && "Số mã từ 1 đến 500.",
    batch &&
      !(Number(form.length) >= 6 && Number(form.length) <= 30) &&
      "Độ dài phần ngẫu nhiên từ 6 đến 30.",
    !(Number(form.maxUser) >= 1) && "Số người được dùng phải từ 1 trở lên.",
    (Number.isNaN(expireDate.getTime()) || expireDate <= new Date()) &&
      "Hạn dùng phải ở tương lai.",
    form.gift.length === 0 && "Chưa thêm quà nào.",
  ].filter(Boolean);

  const save = async () => {
    if (blockers.length) return;
    const body = {
      code,
      maxUser: Number(form.maxUser),
      gift: form.gift,
      expire: form.expire,
      isClanCode: form.isClanCode,
    };
    if (batch) {
      const res = await run(
        () =>
          api.post("/api/admin/giftcodes/batch", {
            ...body,
            prefix,
            length: Number(form.length),
            quantity: Number(form.quantity),
          }),
        (data) => `Đã tạo ${data.length} mã`
      );
      if (res.ok) {
        setCodes(res.result);
        onSaved();
      }
      return;
    }
    const res = await run(
      () =>
        initial
          ? api.put(`/api/admin/giftcodes/${initial.id}`, body)
          : api.post("/api/admin/giftcodes", body),
      initial ? `Đã lưu mã ${code}` : `Đã tạo mã ${code}`
    );
    if (res.ok) {
      onSaved();
      onClose();
    }
  };

  if (codes) {
    const text = codes.join("\n");
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Đã tạo {codes.length} mã</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1 }}>
            Sao chép hoặc tải file để gửi cho người cần phát mã. Danh sách này vẫn tìm lại được ở
            bảng (gõ tiền tố{prefix ? ` "${prefix}"` : ""} vào ô tìm).
          </Alert>
          <MDInput
            fullWidth
            multiline
            minRows={8}
            maxRows={20}
            value={text}
            InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="info" onClick={() => copyText(text, notify)}>
            <Icon>content_copy</Icon>&nbsp;Sao chép tất cả
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => downloadText(text, `giftcode-${prefix || "ma"}-${codes.length}.txt`)}
          >
            <Icon>download</Icon>&nbsp;Tải file .txt
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={onClose}>
            Xong
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  let title = "Tạo gift code";
  if (batch) title = "Tạo nhiều gift code một lúc";
  else if (initial) title = `Sửa gift code ${initial.code}`;

  const who = form.isClanCode ? "bang" : "người";
  const sampleBatch = `${prefix}${"X".repeat(Math.min(Number(form.length) || 0, 30))}`;

  return (
    <ActionDialog
      open
      title={title}
      description={
        batch
          ? "Sinh nhiều mã ngẫu nhiên cùng một gói quà (ví dụ phát cho KOL, mỗi người một mã). Tạo xong sẽ hiện danh sách để sao chép."
          : "Người chơi gõ mã trong game để nhận gói quà bên dưới."
      }
      maxWidth="md"
      busy={busy}
      disabled={blockers.length > 0}
      onClose={onClose}
      onSubmit={save}
      submitText={batch ? `Tạo ${form.quantity || 0} mã` : initial ? "Lưu thay đổi" : "Tạo mã"}
    >
      {initial && initial.currentUser > 0 && (
        <Alert severity="warning">
          Mã này đã có {formatNumber(initial.currentUser)} {who} dùng. Đổi quà chỉ áp dụng cho người
          dùng sau; người đã nhận giữ nguyên quà cũ.
        </Alert>
      )}
      <Grid container spacing={2}>
        {batch ? (
          <>
            <Grid item xs={12} sm={4}>
              <MDInput
                fullWidth
                label="Chữ đầu mỗi mã (không bắt buộc)"
                placeholder="Ví dụ: KOL"
                value={form.prefix}
                onChange={set("prefix")}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <MDInput
                fullWidth
                type="number"
                label="Số ký tự ngẫu nhiên"
                value={form.length}
                onChange={set("length")}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <MDInput
                fullWidth
                type="number"
                label="Tạo bao nhiêu mã (tối đa 500)"
                value={form.quantity}
                onChange={set("quantity")}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              label="Mã người chơi sẽ gõ"
              placeholder="Ví dụ: TANTHU2026"
              value={form.code}
              onChange={set("code")}
              required
              autoFocus
              helperText="Chỉ chữ và số, không dấu. Người chơi phải gõ đúng y hệt."
            />
          </Grid>
        )}
        <Grid item xs={12} sm={batch ? 4 : 6}>
          <MDInput
            fullWidth
            type="number"
            label={batch ? `Mỗi mã cho bao nhiêu ${who} dùng` : `Cho bao nhiêu ${who} dùng`}
            value={form.maxUser}
            inputProps={{ min: 1 }}
            onChange={set("maxUser")}
            helperText={`Mỗi ${who} chỉ dùng được 1 lần`}
          />
        </Grid>
        <Grid item xs={12} sm={batch ? 8 : 6}>
          <MDInput
            fullWidth
            type="datetime-local"
            label="Dùng được tới"
            InputLabelProps={{ shrink: true }}
            value={form.expire}
            onChange={set("expire")}
          />
          <MDBox display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
            {EXPIRE_PRESETS.map((d) => (
              <Chip
                key={d}
                size="small"
                variant="outlined"
                label={`${d} ngày nữa`}
                onClick={() => setForm({ ...form, expire: toLocalInput(addDays(d)) })}
              />
            ))}
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                checked={form.isClanCode}
                onChange={(e) => setForm({ ...form, isClanCode: e.target.checked })}
              />
            }
            label={
              form.isClanCode
                ? "Mã bang: mỗi BANG chỉ dùng 1 lần (người đầu tiên trong bang gõ mã sẽ nhận)"
                : "Mã người chơi: mỗi NGƯỜI nhận 1 lần"
            }
          />
        </Grid>
      </Grid>
      <GiftEditor value={form.gift} onChange={(gift) => setForm((prev) => ({ ...prev, gift }))} />

      <MDBox p={2} borderRadius="lg" sx={{ border: 2, borderColor: "info.main" }}>
        <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
          Kiểm tra lại
        </MDTypography>
        <SummaryRow label={batch ? "Các mã" : "Mã"}>
          {batch ? `${form.quantity || 0} mã dạng ${sampleBatch}` : code || "Chưa nhập"}
        </SummaryRow>
        <SummaryRow label="Số lượt">
          {batch
            ? `Mỗi mã ${formatNumber(Number(form.maxUser) || 0)} ${who}`
            : `${formatNumber(Number(form.maxUser) || 0)} ${who}, mỗi ${who} 1 lần`}
        </SummaryRow>
        <SummaryRow label="Dùng được tới">
          {Number.isNaN(expireDate.getTime())
            ? "Chưa chọn"
            : `${formatDate(expireDate)}${
                daysLeft(expireDate) ? ` (${daysLeft(expireDate)})` : ""
              }`}
        </SummaryRow>
        <SummaryRow label="Quà mỗi lần">
          <GiftPreview gift={form.gift} />
        </SummaryRow>
      </MDBox>
      {blockers.map((b) => (
        <MDTypography key={b} variant="caption" color="text" display="block">
          <Icon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }}>
            info
          </Icon>
          {b}
        </MDTypography>
      ))}
    </ActionDialog>
  );
}

CodeDialog.defaultProps = { initial: null, batch: false };
CodeDialog.propTypes = {
  initial: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    maxUser: PropTypes.number,
    currentUser: PropTypes.number,
    gift: PropTypes.array,
    expire: PropTypes.string,
    isClanCode: PropTypes.bool,
  }),
  batch: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function UsersDialog({ code, onClose }) {
  const { data, loading, error } = useFetch(
    () => api.get(`/api/admin/giftcodes/${code.id}/users`),
    [code.id]
  );
  const clan = data?.isClanCode;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ai đã dùng mã {code.code}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">Không tải được: {error}</Alert>}
        {data && (
          <MDTypography variant="button" color="text" display="block" mb={1}>
            {formatNumber(data.total)} {clan ? "bang" : "người"} đã dùng
            {data.total > data.users.length ? `, hiện ${data.users.length} lần gần nhất` : ""}
          </MDTypography>
        )}
        <AdminTable
          loading={loading}
          rows={[...(data?.users || [])].reverse()}
          rowKey="id"
          emptyText="Chưa ai dùng mã này"
          columns={[
            {
              key: "name",
              label: clan ? "Bang" : "Nhân vật",
              render: (u) =>
                clan ? (
                  u.name || `Bang mã ${u.id}`
                ) : (
                  <Link to={`/players/${u.id}`}>{u.name || `Tài khoản ${u.id}`}</Link>
                ),
            },
          ]}
        />
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

UsersDialog.propTypes = {
  code: PropTypes.shape({ id: PropTypes.number, code: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired,
};

function GiftCodes() {
  const confirm = useConfirm();
  const notify = useNotify();
  const [run, busy] = useRunner();
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [dialog, setDialog] = useState(null);
  const canEdit = useCan(PERMISSIONS.GIFTCODE_EDIT);
  const list = useFetch(
    () => api.get("/api/admin/giftcodes", { q, status, page, size }),
    [q, status, page, size]
  );

  const remove = async (c) => {
    const who = c.isClanCode ? "bang" : "người";
    const ok = await confirm({
      title: `Xoá gift code ${c.code}`,
      message: `${
        c.currentUser > 0
          ? `Đã có ${formatNumber(c.currentUser)} ${who} dùng mã này — họ vẫn giữ quà đã nhận. `
          : "Chưa ai dùng mã này. "
      }Sau khi xoá, người chơi gõ mã sẽ báo không tồn tại. Không khôi phục được.\n\nMuốn chỉ dừng mã thì nên Sửa và đặt hạn dùng về hôm nay.`,
      color: "error",
      confirmText: "Xoá mã",
    });
    if (!ok) return;
    const res = await run(() => api.del(`/api/admin/giftcodes/${c.id}`), `Đã xoá mã ${c.code}`);
    if (res.ok) list.reload(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="giftcodes" />
        <PageCard
          title="Gift code"
          subtitle={list.data ? `${formatNumber(list.data.total)} mã` : null}
          color="success"
          noPadding
          actions={
            canEdit && (
              <>
                <MDButton
                  size="small"
                  variant="contained"
                  color="white"
                  onClick={() => setDialog({ type: "edit" })}
                >
                  <Icon>add</Icon>&nbsp;Tạo mã
                </MDButton>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="white"
                  onClick={() => setDialog({ type: "batch" })}
                >
                  <Icon>library_add</Icon>&nbsp;Tạo nhiều mã
                </MDButton>
              </>
            )
          }
        >
          <MDBox
            px={2}
            pb={1}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setQ(input.trim());
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  label="Tìm mã (gõ một phần cũng được)"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={8} md={3}>
                <SelectField
                  fullWidth
                  label="Trạng thái"
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value);
                  }}
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  <MenuItem value="active">Còn dùng được</MenuItem>
                  <MenuItem value="used">Hết lượt</MenuItem>
                  <MenuItem value="expired">Hết hạn</MenuItem>
                </SelectField>
              </Grid>
              <Grid item xs="auto">
                <MDButton type="submit" variant="gradient" color="info">
                  Tìm
                </MDButton>
              </Grid>
            </Grid>
            {list.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Không tải được danh sách: {list.error}
              </Alert>
            )}
          </MDBox>
          <AdminTable
            loading={list.loading}
            rows={list.data?.items || []}
            rowKey="id"
            page={page}
            size={size}
            total={list.data?.total ?? 0}
            onPageChange={setPage}
            onSizeChange={(s) => {
              setSize(s);
              setPage(1);
            }}
            emptyText={q || status ? "Không có mã nào khớp" : "Chưa có gift code nào — bấm Tạo mã"}
            columns={[
              {
                key: "code",
                label: "Mã",
                render: (c) => (
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {c.code}
                    </MDTypography>
                    <Tooltip title="Sao chép mã">
                      <IconButton
                        size="small"
                        aria-label="Sao chép mã"
                        onClick={() => copyText(c.code, notify)}
                      >
                        <Icon fontSize="small">content_copy</Icon>
                      </IconButton>
                    </Tooltip>
                    {c.isClanCode && <Chip size="small" label="Mã bang" />}
                  </MDBox>
                ),
              },
              {
                key: "status",
                label: "Trạng thái",
                render: (c) => {
                  const st = codeStatus(c);
                  return <Chip size="small" color={st.color} label={st.label} />;
                },
              },
              {
                key: "uses",
                label: "Đã dùng (bấm để xem ai)",
                render: (c) => (
                  <Tooltip title="Xem ai đã dùng mã này">
                    <MDButton
                      size="small"
                      variant="text"
                      color="info"
                      sx={{ px: 0.5, minWidth: 0, textTransform: "none", whiteSpace: "nowrap" }}
                      onClick={() => setDialog({ type: "users", code: c })}
                    >
                      <Icon fontSize="small">group</Icon>&nbsp;
                      {`${formatNumber(c.currentUser)} / ${formatNumber(c.maxUser)} ${
                        c.isClanCode ? "bang" : "người"
                      }`}
                    </MDButton>
                  </Tooltip>
                ),
              },
              {
                key: "gift",
                label: "Quà",
                wrap: true,
                render: (c) =>
                  c.giftLines || c.gift ? (
                    <GiftPreview gift={c.gift} lines={c.giftLines || null} />
                  ) : (
                    <MDTypography variant="caption" color="error">
                      Quà bị lỗi dữ liệu — báo kỹ thuật
                    </MDTypography>
                  ),
              },
              {
                key: "expire",
                label: "Dùng được tới",
                render: (c) => (
                  <MDBox>
                    <MDTypography
                      variant="button"
                      color={c.expired ? "error" : "text"}
                      display="block"
                    >
                      {formatDate(c.expire)}
                    </MDTypography>
                    {!c.expired && (
                      <MDTypography variant="caption" color="text">
                        {daysLeft(c.expire)}
                      </MDTypography>
                    )}
                  </MDBox>
                ),
              },
              {
                key: "actions",
                label: "",
                align: "right",
                render: (c) => (
                  <MDBox display="flex" justifyContent="flex-end" gap={0.5}>
                    {canEdit && (
                      <Tooltip title={c.gift ? "" : "Quà bị lỗi dữ liệu, không sửa được ở đây"}>
                        <span>
                          <MDButton
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={!c.gift}
                            onClick={() => setDialog({ type: "edit", code: c })}
                          >
                            Sửa
                          </MDButton>
                        </span>
                      </Tooltip>
                    )}
                    {canEdit && (
                      <Tooltip title="Xoá mã">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Xoá mã"
                          disabled={busy}
                          onClick={() => remove(c)}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </MDBox>
                ),
              },
            ]}
          />
        </PageCard>
      </MDBox>
      <Footer />

      {dialog?.type === "edit" && (
        <CodeDialog
          initial={dialog.code}
          onClose={() => setDialog(null)}
          onSaved={() => list.reload(true)}
        />
      )}
      {dialog?.type === "batch" && (
        <CodeDialog batch onClose={() => setDialog(null)} onSaved={() => list.reload(true)} />
      )}
      {dialog?.type === "users" && (
        <UsersDialog code={dialog.code} onClose={() => setDialog(null)} />
      )}
    </DashboardLayout>
  );
}

export default GiftCodes;
